import * as React from "react";
import {
  Input,
  Col,
  Form,
  Checkbox,
  Row,
  Button,
  Select,
  Card,
  Space
} from "antd";

import { IOrganisation } from "../../../organisations/model/Organisation";
import SelectInput from "../../../core/ui/components/SelectInput";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { IUser } from "../../../user/model/User";
import { LegalViewStatus } from "../../common/Constants";
import { IStore } from "../../../core/model/runtime/state/Store";
import TranslationTextAreaField from "../../../core/ui/components/TranslationTextAreaField";
import { IDocumentType } from "../../../documentTypes/model/DocumentType";
import { TDocumentTypeIndexCategoryPresentationModel } from "../../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";
import FormSection from "../../../core/ui/components/FormSection";
import DateTimePicker from "../../../core/ui/components/DateTimePicker";
import { DateFormats } from "../../../core/common/Constants";
import moment from "moment";

const FormFields = {
  ID: "id",
  SUMMARY: "summary",
  NOTE: "note",
  STATUS: "status",
  DOCUMENT_TYPE_ID: "documentTypeId",
  ORGANISATION_ID: "organisationId",
  VALID: "valid",
  DATE: "legalViewDate",
  ORGANISATION_INDEXES: "organisationIndexList"
};

const LegalViewCreationForm = ({
  organisations,
  legalViewTypes,
  organisationIndexes,
  legalViewTypeId,
  setOrganisationId,
  translate,
  onSubmit,
  showLegalViewList,
  user,
  setLegalViewTypeId,
  selectedOrganisationId,
  store
}: {
  translate: any;
  organisations: IOrganisation[];
  legalViewTypes: IDocumentType[];
  organisationIndexes: TDocumentTypeIndexCategoryPresentationModel[];
  legalViewTypeId?: number;
  setOrganisationId: (organisationId: number) => void;
  onSubmit;
  showLegalViewList;
  user?: IUser;
  setLegalViewTypeId: (id: number) => void;
  store: IStore;
  selectedOrganisationId?: number;
}) => {
  const { Option } = Select;
  const [form] = Form.useForm();
  function onChangeOrganisation(item) {
    setOrganisationId(item);
  }
  function onChangeLegalViewCode(item) {
    setLegalViewTypeId(item);
  }
  const getCorrectIndexTranslation = (index: any) => {
    const { selectedLanguage } = store.getState<any>();
    const nameTranslation = index.nameTranslations.find(
      (item) => item.languageId === selectedLanguage?.id
    )?.nameTranslation;
    return nameTranslation ?? index.name;
  };

  const getCorrectGroupTranslation = (data?: any) => {
    const { selectedLanguage } = store.getState<any>();
    if (data) {
      const nameTranslation = data.nameTranslations.find(
        (item) => item.languageId === selectedLanguage?.id
      )?.nameTranslation;
      return nameTranslation ?? data.name;
    } else {
      return "";
    }
  };

  function findParent(group, groupList, data) {
    if (group?.parentId) {
      let g = groupList.find((g) => g.id === group?.parentId);
      if (g) {
        data = data + ` 🡐 ${getCorrectGroupTranslation(g)}`;
        return findParent(g, groupList, data);
      }
    } else if (group?.organisationIndexCategory) {
      data =
        data +
        ` 🡐 ${getCorrectGroupTranslation(group?.organisationIndexCategory)}`;
    }
    return data;
  }

  function renderOptions(list) {
    return list.map((index) => {
      let data = getCorrectIndexTranslation(index);
      if (index.groups) {
        let firstGroup = index.groups.find(
          (g) => g.id === index.organisationGroupId
        );
        data = `${getCorrectIndexTranslation(
          index
        )} 🡐 ${getCorrectGroupTranslation(firstGroup)}`;
        data = findParent(firstGroup, index.groups, data);
      }
      return (
        <Option key={index.id} value={index.id} title={data} label={data}>
          {data}
        </Option>
      );
    });
  }
  form.setFieldsValue({
    summaryTranslations: [
      {
        summaryTranslation: "",
        noteTranslation: "",
        languageId: store.getState<any>().selectedLanguage.id,
        valid: true
      }
    ]
  });

  legalViewTypeId &&
    form.setFieldsValue({
      legalViewTypeId: legalViewTypeId
    });
  user &&
    user.isOperator() &&
    form.setFieldsValue({
      organisationId: user.organisationId
    });

  return (
    <Form layout={"vertical"} form={form}>
      <Row align="middle" justify="center">
        <Col xl={20} lg={24} md={24} sm={24} xs={24}>
          <Card bordered={false}>
            <Form.Item name={FormFields.ID} style={{ display: "none" }}>
              <Input />
            </Form.Item>
            <Form.Item
              name={FormFields.VALID}
              valuePropName={"checked"}
              initialValue={true}
              style={{ display: "none" }}
            >
              <Checkbox />
            </Form.Item>
            <Form.Item
              style={{ display: "none" }}
              name={FormFields.STATUS}
              initialValue={LegalViewStatus.IN_PROGRESS}
            >
              <Input />
            </Form.Item>
            <Form.Item
              style={{ display: "none" }}
              name={FormFields.DOCUMENT_TYPE_ID}
            >
              <Input />
            </Form.Item>
            <Space direction="vertical" style={{ width: "100%" }}>
              <FormSection
                title={translate("editors.legal_views.basic_data")}
                subtitle={translate("editors.legal_views.basic_data")}
              >
                <Row gutter={16} style={{ marginBottom: 10, marginTop: 10 }}>
                  <Col xs={24} sm={24}>
                    <div>
                      <Form.Item
                        label={translate(
                          "editors.legal_views.fields.organisation_name"
                        )}
                        name={FormFields.ORGANISATION_ID}
                        initialValue={
                          user?.isOperator() ? user.organisationId : undefined
                        }
                        rules={[
                          {
                            required: true,
                            message: translate(
                              "editors.legal_views.required_messages",
                              {
                                field: translate(
                                  "editors.legal_views.fields.organisation_name"
                                )
                              }
                            )
                          }
                        ]}
                      >
                        <SelectInput
                          placeholder={translate(
                            "editors.legal_views.fields.select_organisation"
                          )}
                          allowClear
                          showSearch={true}
                          notFoundContent={translate("search.no_data")}
                          selectOptionKey={"id"}
                          selectOptionValue={"name"}
                          selectOptions={organisations}
                          onChange={(value) => onChangeOrganisation(value)}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 10, marginTop: 10 }}>
                  <Col xs={24} sm={24}>
                    <div>
                      <Form.Item
                        label={translate(
                          "editors.legal_views.fields.legal_view_type"
                        )}
                        name={FormFields.DOCUMENT_TYPE_ID}
                        className={"mb-0"}
                        rules={[
                          {
                            required: true,
                            message: translate(
                              "editors.legal_views.required_messages",
                              {
                                field: translate(
                                  "editors.legal_views.fields.legal_view_type"
                                )
                              }
                            )
                          }
                        ]}
                      >
                        <SelectInput
                          disabled={
                            form.getFieldValue("organisationId") === undefined
                          }
                          placeholder={translate(
                            "editors.legal_views.fields.select_legal_view_type"
                          )}
                          allowClear
                          defaultActiveFirstOption={true}
                          showSearch={true}
                          notFoundContent={translate("search.no_data")}
                          selectOptionKey={"id"}
                          selectOptionValue={"name"}
                          selectOptions={legalViewTypes}
                          className={"mb-0"}
                          onChange={(value) => onChangeLegalViewCode(value)}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item
                      label={translate("editors.legal_views.date")}
                      name={FormFields.DATE}
                      initialValue={moment(new Date())}
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "editors.case_law_documents.required_messages",
                            {
                              field: translate(
                                "editors.case_law_documents.date"
                              )
                            }
                          )
                        }
                      ]}
                    >
                      <DateTimePicker
                        placeholder={translate("date_picker.placeholder")}
                        format={DateFormats.DATE}
                        showToday={true}
                        className={"input-date-wrapper"}
                        translate={translate}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 10, marginTop: 10 }}>
                  <Col xs={24} sm={24}>
                    <div style={{ marginTop: "3em" }}>
                      <TranslationTextAreaField
                        translate={translate}
                        organisationId={selectedOrganisationId}
                        translationObjectIdFieldName={"caseLawDocumentId"}
                        store={store}
                        form={form}
                        label={translate("editors.legal_views.fields.summary")}
                        noteLabel={translate("editors.legal_views.fields.note")}
                      />
                    </div>
                  </Col>
                </Row>
              </FormSection>
              {organisationIndexes.length > 0 && (
                <FormSection
                  title={translate("editors.legal_views.fields.classification")}
                  subtitle={translate(
                    "editors.legal_views.fields.index_selection"
                  )}
                >
                  {organisationIndexes.map((item, index) => {
                    return (
                      <Row
                        gutter={16}
                        style={
                          organisationIndexes.length === 0
                            ? { display: "none" }
                            : {
                                marginTop: 10,
                                alignItems: "center"
                              }
                        }
                      >
                        <Col
                          lg={15}
                          md={15}
                          sm={16}
                          xs={24}
                          style={{
                            marginTop: 20,
                            alignItems: "center"
                          }}
                        >
                          <Form.Item
                            name={["organisationIndexList", index]}
                            className={"mb-0"}
                            label={translate(
                              "editors.case_decisions.index_data"
                            )}
                            rules={[
                              {
                                required: item.required,
                                message: translate(
                                  "editors.case_decisions.required_messages",
                                  {
                                    field: translate(
                                      "editors.case_decisions.case_decision_type"
                                    )
                                  }
                                )
                              },
                              {
                                validator: (rule, value, callback) => {
                                  if (value && item.maxLength) {
                                    if (value.length > item.maxLength) {
                                      callback(
                                        `Maksimalan broj odabranih deskriptora je ${item.maxLength}`
                                      );
                                    } else if (value.length <= item.maxLength) {
                                      callback();
                                    }
                                  } else {
                                    callback();
                                  }
                                  return;
                                }
                              }
                            ]}
                          >
                            <Select
                              placeholder={translate(
                                "editors.case_law_documents.select_index_data"
                              )}
                              mode="multiple"
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children as any)
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              className={"png-tagbox"}
                            >
                              {renderOptions(item.organisationIndexList)}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  })}
                </FormSection>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
      <Row justify="center">
        <Col
          lg={20}
          md={24}
          sm={24}
          xs={24}
          style={{ textAlign: "right", paddingTop: 20 }}
        >
          <Button
            value=""
            type="link"
            danger
            onClick={() => {
              form.resetFields();
              showLegalViewList();
            }}
          >
            <CloseOutlined /> {translate("data_grid.modal.actions.cancel")}
          </Button>
          <Button
            value=""
            type={"primary"}
            onClick={() => {
              form
                .validateFields()
                .then((values) => {
                  onSubmit(values);
                })
                .catch((info) => {});
            }}
          >
            <CheckOutlined /> {translate("data_grid.modal.actions.save")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
export default LegalViewCreationForm;
